import { getAuthHeaders, authedFetch } from './authService';

export type NodeApiItem = {
  id: number;
  name: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  geofence_radius_m?: number;
  minimum_guard_count?: number;
  status?: string;
  active?: boolean;
  city?: string;
  current_guard_occupancy?: number;
};

export type NodesApiResponse = {
  nodes: NodeApiItem[];
  meta?: any;
};
const HOSTNAME = process.env.REACT_APP_API_HOSTNAME ?? 'http://localhost:8000';

const notifyServiceError = (msg: string) => {
  try { window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: msg } })); } catch (e) { /* ignore */ }
};

export async function getNodes(page?: number, pageSize?: number, search?: string, city?: string): Promise<NodesApiResponse> {
  const params = new URLSearchParams();
  if (typeof page === 'number') params.append('page', String(page));
  if (typeof pageSize === 'number') params.append('page_size', String(pageSize));
  if (search) params.append('search', search);
  // KAN-137: use the dedicated `city` filter on the backend for the city
  // autocomplete. It joins on the City FK and naturally excludes nodes with
  // city = NULL, unlike `search` which is OR-fuzzy across name/address/city.
  if (city) params.append('city', city);
  const q = params.toString();
  const url = `${HOSTNAME}/adminportal/nodes${q ? `?${q}` : ''}`;
  const res = await authedFetch(url, { method: 'GET', headers: { ...getAuthHeaders() } });
  if (!res.ok) {
    const txt = await res.text();
    const msg = `Failed to fetch nodes: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  const body = await res.json();
  // expected shape: { success, message, data: { nodes, meta } }
  const nodes = body?.data?.nodes ?? [];
  const meta = body?.data?.meta ?? body?.meta ?? null;
  return { nodes, meta };
}

/**
 * Fetch list of states for locations
 * GET /adminportal/locations/states
 */
export async function getStates(): Promise<Array<{ id: number; name: string }>> {
  const url = `${HOSTNAME}/adminportal/locations/states`;
  const res = await authedFetch(url, { method: 'GET', headers: { ...getAuthHeaders() } });
  if (!res.ok) return [];
  const body = await res.json().catch(() => null);
  return body?.data?.states ?? body?.states ?? [];
}

/**
 * Fetch list of cities for locations
 * GET /adminportal/locations/cities
 */
export async function getCities(): Promise<Array<{ id: number; name: string }>> {
  const url = `${HOSTNAME}/adminportal/locations/cities`;
  const res = await authedFetch(url, { method: 'GET', headers: { ...getAuthHeaders() } });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    const msg = `Failed to fetch cities: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  const body = await res.json().catch(() => null);
  // expecting { success, message, data: { cities: [...] } }
  const cities = body?.data?.cities ?? body?.cities ?? body?.data ?? [];
  return Array.isArray(cities) ? cities : [];
}

/**
 * Fetch list of districts for locations
 * GET /adminportal/locations/districts
 */
export async function getDistricts(): Promise<string[]> {
  const url = `${HOSTNAME}/adminportal/locations/districts`;
  const res = await authedFetch(url, { method: 'GET', headers: { ...getAuthHeaders() } });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    const msg = `Failed to fetch districts: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  const body = await res.json().catch(() => null);
  // support shapes: { data: { districts: [...] } } or { districts: [...] } or raw array
  const list = body?.data?.districts ?? body?.districts ?? body ?? [];
  if (Array.isArray(list)) {
    // map objects like { id, name } or strings to string list
    return list.map((it: any) => (typeof it === 'string' ? it : it?.name ?? String(it)));
  }
  return [];
}

/**
 * Set node active/inactive
 * POST /adminportal/nodes/:id/active
 * body: { active: boolean }
 */
export async function setNodeActive(nodeId: number, active: boolean): Promise<any> {
  const url = `${HOSTNAME}/adminportal/nodes/${nodeId}/active`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ active }),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const txt = body?.message ?? await res.text().catch(() => '');
    const msg = `Failed to set node active: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  return body;
}

/**
 * Create a new node
 * POST /adminportal/nodes/create
 */
export async function createNode(payload: any): Promise<any> {
  const url = `${HOSTNAME}/adminportal/nodes/create`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const txt = body?.message ?? await res.text().catch(() => '');
    const msg = `Failed to create node: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  return body;
}

/**
 * Update a node with only the fields that changed
 * PATCH /adminportal/nodes/:id/edit
 */
export async function updateNode(nodeId: number, payload: any): Promise<any> {
  const url = `${HOSTNAME}/adminportal/nodes/${nodeId}/edit`;
  const res = await authedFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const txt = body?.message ?? await res.text().catch(() => '');
    const msg = `Failed to update node: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  return body;
}
